"use client";

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import api, { recordOf } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UserProfile {
  id: string;
  name?: string;
  full_name?: string;
  preferredName?: string;
  username?: string;
  email?: string;
  bio?: string;
  avatar_url?: string | null;
  avatar?: string;
  phone?: string;
  institution?: string;
  level?: string;
  department?: string;
  course?: string;
  assignedCourse?: string;
  [key: string]: unknown;
}

export interface QuotaInfo {
  free_remaining: number;
  extra_credits: number;
  total_used?: number;
}

export interface UserCourse {
  id: string;
  course_code: string;
  course_name?: string;
  department_code?: string;
  level?: number;
  enrolled?: boolean;
  [key: string]: unknown;
}

interface UserDataContextType {
  profile: UserProfile | null;
  courses: UserCourse[];
  quota: QuotaInfo;
  loading: boolean;
  error: string | null;

  // Actions
  updateProfile: (data: Partial<UserProfile>) => Promise<void>;
  refetchProfile: () => Promise<void>;
  refetchCourses: () => Promise<void>;
  refetchQuota: () => Promise<void>;
  refetchAll: () => Promise<void>;
  invalidateProfile: () => void;
  invalidateQuota: () => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const UserDataContext = createContext<UserDataContextType | null>(null);

export function useUserData(): UserDataContextType {
  const ctx = useContext(UserDataContext);
  if (!ctx) {
    throw new Error("useUserData must be used within UserDataProvider");
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function UserDataProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [courses, setCourses] = useState<UserCourse[]>([]);
  const [quota, setQuota] = useState<QuotaInfo>({
    free_remaining: 0,
    extra_credits: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Mock fallback — used when the backend API is unreachable so that pages
  // still render with placeholder data instead of showing nothing. This is
  // NOT real data; it is only meant to keep the UI usable during outages.
  // Remove this once the API is restored and returning real profiles.
  const MOCK_PROFILE: UserProfile = useMemo(
    () => ({
      id: user?.id || "mock-user",
      name: user?.name || "Test User",
      full_name: user?.name || "Test User",
      preferredName: user?.preferredName,
      username: user?.username || "testuser",
      email: user?.email || "test@example.com",
      bio: "Backend API is temporarily unavailable. This is mock data so you can still study.",
      avatar_url: null,
      avatar: user?.avatar,
      institution: user?.assignedCourse
        ? `${user.assignedCourse} University`
        : "University",
      level: "200",
      department: "Academic",
      course: user?.assignedCourse || "General Studies",
      assignedCourse: user?.assignedCourse,
      credits: 100,
      study_streak: 3,
      profile_complete: true,
      onboarding_completed: true,
      is_premium: false,
    }),
    [user],
  );

  const MOCK_COURSES: UserCourse[] = useMemo(
    () => [
      {
        id: "mock-course-1",
        course_code: user?.assignedCourse || "GEN101",
        course_name: "General Studies",
        department_code: "GEN",
        level: 100,
        enrolled: true,
      },
    ],
    [user],
  );

  // Fetch profile -----------------------------------------------------------
  const refetchProfile = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.getUserProfile();
      setProfile(recordOf(res));
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to load profile";
      setError(msg);
      // FALLBACK: set mock profile so pages still render during API outage.
      setProfile(MOCK_PROFILE);
    }
  }, [user, MOCK_PROFILE]);

  // Fetch courses -----------------------------------------------------------
  const refetchCourses = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.getCourses();
      const data = recordOf(res);
      setCourses(
        Array.isArray(data) ? data : (data?.enrolled ?? data?.courses ?? []),
      );
    } catch {
      // courses are non-critical; fail silently
      // FALLBACK: use mock courses so lists still render during API outage.
      setCourses(MOCK_COURSES);
    }
  }, [user, MOCK_COURSES]);

  // Fetch quota -------------------------------------------------------------
  const refetchQuota = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.getUserQuota();
      const data = recordOf(res);
      setQuota({
        free_remaining: Number(data?.free_remaining ?? 0),
        extra_credits: Number(data?.extra_credits ?? 0),
        total_used: Number(data?.total_used ?? 0),
      });
    } catch {
      // quota is non-critical; fail silently
    }
  }, [user]);

  // Fetch everything at once ----------------------------------------------
  const refetchAll = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    // Use allSettled so one failed fetch doesn't skip the others.
    await Promise.allSettled([
      refetchProfile().catch(() => null),
      refetchCourses().catch(() => null),
      refetchQuota().catch(() => null),
    ]);
    setLoading(false);
  }, [user, refetchProfile, refetchCourses, refetchQuota]);

  // Update profile and keep local state in sync ---------------------------
  const updateProfile = useCallback(async (data: Partial<UserProfile>) => {
    setLoading(true);
    try {
      const updated = await api.updateProfile(data);
      setProfile((prev) => ({ ...prev, ...recordOf(updated) }));
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Failed to update profile";
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Invalidation helpers (force refetch on next access) -------------------
  const invalidateProfile = useCallback(() => setProfile(null), []);
  const invalidateQuota = useCallback(
    () => setQuota({ free_remaining: 0, extra_credits: 0 }),
    [],
  );

  // Initial fetch when user changes ----------------------------------------
  useEffect(() => {
    if (user) {
      refetchAll();
    } else {
      setProfile(null);
      setCourses([]);
      setQuota({ free_remaining: 0, extra_credits: 0 });
      setLoading(false);
    }
  }, [user, refetchAll]);

  const value = useMemo(
    () => ({
      profile,
      courses,
      quota,
      loading,
      error,
      updateProfile,
      refetchProfile,
      refetchCourses,
      refetchQuota,
      refetchAll,
      invalidateProfile,
      invalidateQuota,
    }),
    [
      profile,
      courses,
      quota,
      loading,
      error,
      updateProfile,
      refetchProfile,
      refetchCourses,
      refetchQuota,
      refetchAll,
      invalidateProfile,
      invalidateQuota,
    ],
  );

  return (
    <UserDataContext.Provider value={value}>
      {children}
    </UserDataContext.Provider>
  );
}

/**
 * Convenience hook: returns only the profile portion of user data.
 * Use this in profile page, header, sidebar, etc.
 */
export function useUserProfile() {
  const { profile, loading, error } = useUserData();
  return { profile, loading, error };
}

/**
 * Convenience hook: returns only the quota portion of user data.
 * Use this wherever quota/credits are displayed.
 */
export function useUserQuota() {
  const { quota, refetchQuota } = useUserData();
  return { quota, refetchQuota };
}

/**
 * Convenience hook: returns only the courses portion of user data.
 * Use this in dashboard, course lists, etc.
 */
export function useUserCourses() {
  const { courses, refetchCourses } = useUserData();
  return { courses, refetchCourses };
}
